import { ConstantValues } from "common/constants";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { IUserDetails } from "models/IUserDetails";
import { IWorkspace } from "models/IWorkspace";

export const WORKSPACE_FORM_LABELS = {
  CREATE_TITLE: "Add Workspace",
  EDIT_TITLE: "Update Workspace",
  WORKSPACE_NAME: "Workspace Name",
  DESCRIPTION: "Description",
  OWNERS: "Owners",
  TEAM_MEMBERS: "Team Members",
  CANCEL: "Cancel",
  SAVE: "Save",
  SAVING: "Saving",
  CREATING: "Creating",
} as const;

export const WORKSPACE_ERROR_MESSAGES = {
  DUPLICATE_NAME: "A workspace with this name already exists.",
  OWNER_REQUIRED: "At least one owner is required.",
  CANNOT_REMOVE_SELF: "You cannot remove yourself from the team.",
  SAVE_ERROR: "An error occurred while saving. Please try again.",
} as const;

export const WORKSPACE_FIELD_LIMITS = {
  NAME_MAX_LENGTH: 150,
  NAME_WARNING_THRESHOLD: 120,
  DESCRIPTION_MAX_LENGTH: 2000,
  DESCRIPTION_WARNING_THRESHOLD: 1800,
} as const;

export const WORKSPACE_PICKER_IDS = {
  OWNERS: "workspace-owners",
  TEAM_MEMBERS: "user",
} as const;

export const optionToUserDetails = (option: CustomOption): IUserDetails => ({
  userId: option.id ?? ConstantValues.EMPTY_STRING,
  displayName: option.text ?? ConstantValues.EMPTY_STRING,
});

export const userDetailsToOption = (user: IUserDetails): CustomOption => ({
  id: user.userId,
  text: user.displayName,
  key: user.userId,
});

export const deduplicateOptions = (options: CustomOption[]): CustomOption[] => {
  return Array.from(new Map(options.map((item) => [item.id, item])).values());
};

export const validateRequiredFields = (
  name: string,
  clientName: string,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!name.trim()) {
    errors.name = `${WORKSPACE_FORM_LABELS.WORKSPACE_NAME} is required`;
  }
  if (!clientName.trim()) {
    errors.clientName = `${WORKSPACE_FORM_LABELS.DESCRIPTION} is required`;
  }

  return errors;
};

export const validateDuplicateName = (
  name: string,
  workspaces: IWorkspace[],
  currentWorkspaceId?: string,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const isDuplicate = workspaces.some(
    (w) =>
      w.name.trim().toLowerCase() === name.trim().toLowerCase() &&
      w.id !== currentWorkspaceId,
  );

  if (isDuplicate) {
    errors.name = WORKSPACE_ERROR_MESSAGES.DUPLICATE_NAME;
  }

  return errors;
};

export const validateOwners = (
  owners: CustomOption[],
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (owners.length === 0) {
    errors.owners = WORKSPACE_ERROR_MESSAGES.OWNER_REQUIRED;
  }
  return errors;
};

export const validateSelfRemoval = (
  currentUserId: string,
  originalMemberIds: string[],
  currentMemberIds: string[],
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const wasInTeamBefore = originalMemberIds.includes(currentUserId);
  const isNowRemoved = !currentMemberIds.includes(currentUserId);

  if (wasInTeamBefore && isNowRemoved) {
    errors.teamMembers = WORKSPACE_ERROR_MESSAGES.CANNOT_REMOVE_SELF;
  }

  return errors;
};
